import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Button } from "../../../app/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../app/components/ui/card";
import { api } from "../../../lib/api";
import { useAuth } from "../../../providers/AuthProvider";
import { DealerForm, type DealerFormValues } from "../components/DealerForm";
import { useDealers } from "../hooks/useDealers";

export function DealerUpsertPage() {
  const { token } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { dealers, loading, reload } = useDealers();
  const [deleting, setDeleting] = useState(false);

  const mode: "create" | "edit" = id ? "edit" : "create";

  const current = useMemo(() => {
    if (!id) return null;
    return dealers.find((d) => String(d.id) === String(id)) ?? null;
  }, [dealers, id]);

  useEffect(() => {
    if (mode === "edit" && !loading && id && !current) {
      // No show endpoint; fall back to list lookup.
      void reload();
    }
  }, [mode, loading, id, current, reload]);

  const defaults: Partial<DealerFormValues> | undefined = useMemo(() => {
    if (!current) return undefined;
    return {
      name: current.name,
      status: current.status ?? "active",
      contact_person: current.contact_person ?? "",
      phone: current.phone ?? "",
      email: current.email ?? "",
      gst_number: current.gst_number ?? "",
      address: current.address ?? "",
      lat: current.lat ?? "",
      lng: current.lng ?? "",
      place_id: current.place_id ?? "",
    };
  }, [current]);

  async function onDelete() {
    if (!token || !id) return;
    const ok = window.confirm("Delete this dealer?");
    if (!ok) return;
    setDeleting(true);
    try {
      await api.dealers.remove({ token, id });
      navigate("/dealers");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-foreground mb-1">
            {mode === "create" ? "Add dealer" : "Edit dealer"}
          </h1>
          <p className="text-muted-foreground">
            {mode === "create" ? "Create a new dealer." : "Update dealer details."}
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline">
            <Link to="/dealers">Back</Link>
          </Button>
          {mode === "edit" ? (
            <Button variant="destructive" onClick={() => void onDelete()} disabled={deleting}>
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          ) : null}
        </div>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>{mode === "create" ? "Dealer details" : "Update dealer"}</CardTitle>
          <CardDescription>These fields are used when creating inventory entries.</CardDescription>
        </CardHeader>
        <CardContent>
          <DealerForm
            key={mode === "edit" && id ? `${id}-${current ? "loaded" : "pending"}` : "create"}
            mode={mode}
            dealerId={id}
            defaultValues={defaults}
            onSuccess={() => {
              navigate("/dealers");
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
}

